import { useState } from 'react'

import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material'

import { useNotificationsStore } from '@/store/useNotificationsStore'
import { formatRelativeTime } from '@/utils/format'
import { AgentStatusToggle } from '@/features/agents/components/AgentStatusToggle'
import { useAgentStatus } from '@/features/agents/hooks/useAgentStatus'
import { ThemeToggle } from '@/components/common/ThemeToggle'

import { useAuth } from '@/providers/AuthProvider'

interface TopBarProps {
  onOpenSidebar?: () => void
  drawerWidth?: number
}

export const TopBar = ({ onOpenSidebar, drawerWidth = 264 }: TopBarProps) => {
  const { user, logout } = useAuth()
  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useNotificationsStore()
  const { status, updateStatus } = useAgentStatus()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    useState<HTMLElement | null>(null)

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorEl(null)
  }

  const handleOpenNotifications = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget)
  }

  const handleCloseNotifications = () => {
    setNotificationsAnchorEl(null)
  }

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        width: { xs: '100%', lg: `calc(100% - ${drawerWidth}px)` },
        ml: { lg: `${drawerWidth}px` },
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ gap: 2 }}>
        <IconButton
          color="inherit"
          sx={{ display: { lg: 'none' } }}
          onClick={onOpenSidebar}
          aria-label="Open navigation"
        >
          <MenuRoundedIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {user?.name ? `Welcome back, ${user.name}` : 'KiroSupport Admin'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor live conversations, manage agents, and control AI behaviour.
          </Typography>
        </Box>
        <AgentStatusToggle status={status} onStatusChange={updateStatus} />
        <ThemeToggle />
        <IconButton color="inherit" onClick={handleOpenNotifications}>
          <Badge color="error" badgeContent={unreadCount} overlap="circular">
            <NotificationsNoneRoundedIcon />
          </Badge>
        </IconButton>
        <IconButton color="inherit" onClick={handleOpenUserMenu} sx={{ p: 0.5 }}>
          <Avatar
            src={user?.avatarUrl}
            alt={user?.name}
            sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}
          >
            {user?.name?.slice(0, 1).toUpperCase() ?? 'A'}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={notificationsAnchorEl}
          open={Boolean(notificationsAnchorEl)}
          onClose={handleCloseNotifications}
          PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stay on top of new chat requests and AI updates.
            </Typography>
          </Box>
          <Divider />
          {notifications.length === 0 ? (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                You are all caught up! New notifications will appear here.
              </Typography>
            </Box>
          ) : (
            notifications.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => markAsRead(notification.id)}
                sx={{
                  alignItems: 'flex-start',
                  gap: 1,
                  opacity: notification.read ? 0.6 : 1,
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {notification.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {formatRelativeTime(notification.createdAt)}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          )}
          {notifications.length ? (
            <>
              <Divider />
              <MenuItem
                onClick={() => {
                  markAllAsRead()
                  handleCloseNotifications()
                }}
              >
                Mark all as read
              </MenuItem>
            </>
          ) : null}
        </Menu>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseUserMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem disabled>
            <Box>
              <Typography variant="subtitle2">{user?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              void logout()
              handleCloseUserMenu()
            }}
          >
            <ListItemIcon>
              <LogoutRoundedIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}
